import { istAbgeleitet } from '../domain/abgeleitet';
import { felderAusTeilen } from '../domain/dateiname';
import { svgToStrokes } from '../domain/ink';
import { eqFrac, frac, fromNumber, scaleStep, toNumber, type Fraction } from '../domain/points';
import {
  LISTEN_LOG_TYPEN, medienAnzahl, type MedienLogTyp,
} from '../domain/medienzahl';
import { criterionKey, emptyAssessment, wordKey, type Assessment } from '../domain/scoring';
import { emptyComment, type CommentBox, type MediaKind } from '../domain/types';
import {
  type LogScope, deleteFileMitSignatur, listFiles, readText } from '../platform/storage';
import { appendLog, latestLogs, type LogDraft } from './examLog';
import type { Store } from './port';
import type {
  ExamAnswerRow, ExamItemContainerRow, ExamItemRow, ExamLogRow, ExamPartRow, ExamQuestionRow,
} from './rows';
import { logTypeId } from './seed';

/**
 * Übersetzt zwischen dem `Assessment` der Oberfläche und `ExamLog`.
 *
 * Der Bogen kennt Aufgaben und Zeilennummern, Relay kennt `ExamItem`, `ExamQuestion` und
 * `ExamAnswer`. Der `TaskIndex` hält die Brücke — einmal je Prüfung gebaut, danach nur noch
 * nachgeschlagen.
 */

export interface TaskEntry {
  itemId: string;
  questionId: string;
  /** Antwortzeilen nach `orderNo`: Checklistenzeilen bzw. Kriterien. */
  answers: { id: string; max: Fraction; steps: number }[];
}

export type TaskIndex = Map<string, TaskEntry>;

export interface LogContext {
  examID: string;
  candidateID: string;
  examinerID: string | null;
  actorID: string | null;
}

export async function buildTaskIndex(store: Store, examId: string): Promise<TaskIndex> {
  const index: TaskIndex = new Map();
  const parts = await store.all<ExamPartRow>('ExamPart', { examID: examId });
  for (const part of parts) {
    const container = await store.all<ExamItemContainerRow>('ExamItemContainer', { examPartID: part.id });
    for (const c of container) {
      const items = await store.all<ExamItemRow>('ExamItem', { examItemContainerID: c.id });
      for (const item of items) {
        const [frage] = await store.all<ExamQuestionRow>('ExamQuestion', { examItemID: item.id });
        if (!frage) continue;
        const answers = (await store.all<ExamAnswerRow>('ExamAnswer', { examQuestionID: frage.id }))
          .sort((a, b) => a.orderNo - b.orderNo)
          .map((a) => ({
            id: a.id,
            max: frac(a.maxPointsNumerator, a.maxPointsDenominator),
            steps: a.scalaValues ? (JSON.parse(a.scalaValues) as string[]).length : 0,
          }));
        index.set(item.id, { itemId: item.id, questionId: frage.id, answers });
      }
    }
  }
  return index;
}

const MEDIA_FELD = { audio: 'audios', photo: 'photos', video: 'videos' } as const;

/** Die Indexe der markierten Wortgruppen einer Aufgabe, aufsteigend. */
function wortIndexe(a: Assessment, taskId: string): number[] {
  return Object.keys(a.pickedWords)
    .filter((k) => a.pickedWords[k] && k.startsWith(taskId + '#'))
    .map((k) => Number(k.slice(taskId.length + 1)))
    .sort((x, y) => x - y);
}

interface Dateiname {
  /** Zeitanteil `yyyy-MM-dd-HH-mm-ss-SSS` — string-sortierbar, weil fest gepolstert. */
  date: string;
  candidateId: string;
  taskId: string | null;
  /** Nur bei Stationskommentaren gesetzt — dort führt keine `taskID` (s. `buildFileName`). */
  stationId: string | null;
}

/**
 * Liest die Zuordnung aus dem Dateinamen (`platform/storage.ts::buildFileName`):
 * `<art>_date=…_examID=…_candidateID=…_examinerID=…_actorID=…_stationID=…_taskID=…_itemID=…`.
 * Ein Name, der nicht diesem Schema folgt, liefert `null` und fällt beim Filtern einfach
 * heraus — eine fremde Datei im Ordner darf nicht abstürzen.
 *
 * ⚠️ **Teile ohne `=` werden übersprungen.** Der erste Teil ist die **Art** und trägt keins
 * (seit 2026-09-14). Ohne diese Wache stünde sie als Feld `phot → photo` in der Karte — heute
 * folgenlos, aber es ist genau die Art von stillem Unsinn, die später eine echte Kennung
 * verdrängt.
 *
 * ⚠️ **Ältere Dateien tragen die kürzere Form** (ohne Art, ohne `actorID`, ohne `itemID`). Sie
 * werden hier weiterhin gelesen: gefordert sind nur `date` und `candidateID`.
 */
function dateiname(pfad: string): Dateiname | null {
  const basis = pfad.slice(pfad.lastIndexOf('/') + 1).replace(/\.[^./]+$/, '');
  /*
   * ⚠️ **Zerlegt wird an einer Stelle für alle** (`domain/dateiname.ts::felderAusTeilen`, seit
   * 2026-09-21). Der Name trägt seither kurze Schlüssel und kompakte Kennungen; das Zerlegen
   * gibt die ausgeschriebene Form zurück, sodass hier unverändert `candidateID`,
   * `examItemID` und der Rest gelesen werden — und ältere Dateien genauso wie bisher.
   */
  const felder = new Map(Object.entries(felderAusTeilen(basis)));
  const date = felder.get('date');
  const candidateId = felder.get('candidateID');
  if (!date || !candidateId) return null;
  return {
    date,
    candidateId,
    /*
     * ⚠️ **Beide Gleise, alle Formen.** Seit dem 2026-09-17 schreibt die Ablage im Relay-Modus
     * `examItemContainerID`/`examItemID` und im Classic-Modus `stationID`/`taskID`
     * (`platform/storage.ts`). Ältere Dateien tragen zusätzlich `itemID` mit demselben Wert wie
     * `taskID`. Hier wird **gelesen**, nicht geschrieben: ein Moduswechsel darf keine Aufnahme
     * entwerten, die gestern abgelegt wurde — sie liegt im selben Ordner wie die von heute.
     */
    taskId: felder.get('examItemID') ?? felder.get('taskID') ?? felder.get('itemID') ?? null,
    stationId: felder.get('examItemContainerID') ?? felder.get('stationID') ?? null,
  };
}

/** `HH:MM` aus dem Zeitanteil `yyyy-MM-dd-HH-mm-ss-SSS` des Dateinamens. */
function uhrzeit(date: string): string {
  const [, , , stunde, minute] = date.split('-');
  return (stunde ?? '00') + ':' + (minute ?? '00');
}

/**
 * Die jüngste Datei einer Art zu genau diesem Prüfling und dieser Aufgabe — nach demselben
 * Muster wie oben (`dateiname`) gefiltert und sortiert. Für die Handschrift gibt es keine
 * `Media`-Zeile, die auf die Datei zeigt (s. `sichereHandschrift` in `CommentSheet.tsx`);
 * Zuordnung und „welche ist die aktuelle" kommen deshalb ausschließlich aus dem Dateinamen.
 */
async function jüngsteDatei(
  kind: MediaKind | 'handwriting',
  candidateId: string,
  taskId: string,
): Promise<{ pfad: string; name: Dateiname } | undefined> {
  return (await alleDateien(kind, candidateId, { taskId })).pop();
}

/**
 * Alle Dateien einer Art zu genau diesem Prüfling und diesem Ziel, aufsteigend nach Zeit.
 *
 * **Zwei Ziele.** Eine Aufgabe (`{ taskId }`, Ordner `task-comment`) oder eine Station
 * (`{ stationId }`, Ordner `station-comment`). Beide Wege trennen sich nur hier; alles
 * darüber arbeitet mit demselben Kommentarkasten.
 */
async function alleDateien(
  kind: MediaKind | 'handwriting',
  candidateId: string,
  ziel: { taskId: string } | { stationId: string },
): Promise<{ pfad: string; name: Dateiname }[]> {
  const bezug: LogScope = 'taskId' in ziel ? 'task-comment' : 'station-comment';
  const trifft = (name: Dateiname) =>
    'taskId' in ziel ? name.taskId === ziel.taskId : name.taskId === null && name.stationId === ziel.stationId;

  return (await listFiles(bezug, kind))
    /*
     * ⚠️ **Abgeleitete Dateien liegen im Ordner ihrer Aufnahme** — die Markierebene neben
     * ihrem Foto, das Vorschaubild neben seinem Video, die Beglaubigung neben beidem
     * (`domain/abgeleitet.ts`). Ihr Name trägt dieselbe Zuordnung, sie kämen also durch jeden
     * Filter darunter: der Bogen zeigte ein zweites Foto, das niemand aufgenommen hat, und
     * seit dem 2026-09-14 ginge diese Zahl als `value` an den Server
     * (`domain/medienzahl.ts::medienAnzahl` zählt genau diese Listen).
     */
    .filter((pfad) => !istAbgeleitet(pfad))
    .map((pfad) => ({ pfad, name: dateiname(pfad) }))
    .filter((x): x is { pfad: string; name: Dateiname } => x.name !== null)
    .filter((x) => x.name.candidateId === candidateId && trifft(x.name))
    .sort((x, y) => (x.name.date < y.name.date ? -1 : x.name.date > y.name.date ? 1 : 0));
}

/**
 * Entfernt alle Handschriftseiten eines Prüflings zu einer Aufgabe — für den „Leeren"-Knopf
 * (`CommentSheet.tsx`). Ohne diesen Aufräumschritt bliebe die zuletzt geschriebene Seite die
 * „jüngste" im Ordner und käme beim nächsten Öffnen unverändert zurück, obwohl der Prüfer sie
 * gerade gelöscht hat — es gibt keine `Media`-Zeile, die stattdessen gelöscht werden könnte.
 *
 * Bewusst **alle** passenden Dateien, nicht nur die jüngste: über mehrere Sitzungen hinweg
 * schreibt `sichereHandschrift` bei jedem Schließen eine neue Datei, nie dieselbe erneut. Nur
 * die jüngste zu löschen ließe eine ältere als neue „jüngste" zurück — dieselbe Wiederauferstehung,
 * nur eine Stufe später.
 */
export async function deleteHandwriting(
  candidateId: string,
  zielId: string,
  anStation = false,
): Promise<void> {
  const dateien = await alleDateien('handwriting', candidateId, anStation ? { stationId: zielId } : { taskId: zielId });
  for (const datei of dateien) await deleteFileMitSignatur(datei.pfad);
}

/**
 * Schreibt den `ExamLog`-Eintrag für eine bereits geschriebene Handschriftseite. Anders als der
 * übrige Aufbau von `saveAssessment` oben (Diff aus zwei Assessment-Ständen, der die Datei erst
 * hinterher im Verzeichnis sucht) bekommt diese Funktion den Dateinamen direkt vom Aufrufer, der
 * ihn gerade von `saveText` zurückbekommen hat. `sichereHandschrift` ruft sie mit `await` auf —
 * das erzwingt die Reihenfolge „erst Datei, dann Eintrag" durch den Programmablauf, statt sie zu
 * erhoffen (das war der Fehler in der vorigen Fassung).
 *
 * Kennt der `TaskIndex` die Aufgabe nicht (die Prüfung ist noch nicht in die Ablage importiert),
 * bleibt es beim Schreiben der Datei — ohne `ExamItem`-Kennung gäbe es nichts Gültiges, worauf
 * der Eintrag zeigen könnte.
 */
export async function logHandwriting(
  store: Store,
  index: TaskIndex,
  ctx: LogContext,
  taskId: string,
  /** 1, solange eine Seite beschrieben ist; 0, wenn sie geleert wurde. */
  anzahl: number,
  anStation = false,
): Promise<void> {
  await logMedienAnzahl(store, index, ctx, taskId, 'HANDWRITING_COMMENT', anzahl, anStation);
}

/**
 * Der Protokolleintrag für einen Medienstand: so viele Aufnahmen dieser Art hängen jetzt an der
 * Aufgabe (oder an der Station).
 *
 * ⚠️ **Die Zahl ist der Gesamtstand, nicht ein Zuwachs** — der Server wertet je (Item, Typ) nur
 * den jüngsten Eintrag aus. Sie wird an einer einzigen Stelle gerechnet
 * (`domain/medienzahl.ts`); liefen die Zählungen der Schreiber auseinander, überschrieben sie
 * einander mit verschiedenen Zahlen.
 *
 * ⚠️ **Die Markierebene bekommt keinen Eintrag.** Sie gehört zu ihrem Foto (Entscheidung des
 * Nutzers, 2026-09-14) und zählt nicht extra; ihre Zeile trüge nur die unveränderte Fotoanzahl
 * und verdrängte dabei den jüngsten Eintrag. Übertragen wird die Datei trotzdem
 * (`server/anhaenge.ts`, Art `overlay`).
 */
export async function logMedienAnzahl(
  store: Store,
  index: TaskIndex,
  ctx: LogContext,
  taskId: string,
  typ: MedienLogTyp,
  anzahl: number,
  /**
   * Bei einem **Stationskommentar** zeigt der Eintrag auf die Station statt auf eine Aufgabe;
   * `taskId` ist dann die Stationskennung und steht in keinem `TaskIndex` (seit 2026-09-13).
   */
  anStation = false,
): Promise<void> {
  const eintrag = index.get(taskId);
  if (!anStation && !eintrag) return;
  await appendLog(store, {
    examID: ctx.examID,
    candidateID: ctx.candidateID,
    examinerID: ctx.examinerID,
    actorID: ctx.actorID,
    type: typ,
    referenceType: anStation ? 'EXAM_ITEM_CONTAINER' : 'EXAM_ITEM',
    referenceIdentifier: anStation ? taskId : eintrag!.itemId,
    value: String(anzahl),
  });
}

/**
 * Schreibt, was sich zwischen zwei Ständen geändert hat. Rückgabe: die Zahl der Zeilen.
 *
 * Nur Unterschiede — `ExamLog` wächst sonst bei jedem Tastendruck um den ganzen Bogen.
 */
export async function saveAssessment(
  store: Store,
  index: TaskIndex,
  ctx: LogContext,
  vorher: Assessment,
  nachher: Assessment,
  /**
   * Die Station, an der bewertet wird — Ziel des Stationskommentars (seit 2026-09-13). Fehlt
   * sie, wird er schlicht nicht geschrieben; alles Übrige bleibt unberührt.
   */
  stationId?: string,
): Promise<number> {
  const entwürfe: LogDraft[] = [];
  const grund = { examID: ctx.examID, candidateID: ctx.candidateID, examinerID: ctx.examinerID, actorID: ctx.actorID };

  for (const [taskId, eintrag] of index) {
    // — Häkchen: angehakt ⇒ Höchstwert der Zeile, sonst 0. Auch die 0 wird geschrieben,
    //   nur so unterscheidet sich „bewusst nicht gegeben" von „noch nicht bewertet". —
    const altGehakt = vorher.checked[taskId] ?? [];
    const neuGehakt = nachher.checked[taskId] ?? [];
    eintrag.answers.forEach((antwort, i) => {
      if (antwort.steps > 0) return; // Kriterium, kein Häkchen
      if (!(taskId in nachher.checked)) return;
      if (Boolean(altGehakt[i]) === Boolean(neuGehakt[i]) && taskId in vorher.checked) return;
      entwürfe.push({
        ...grund,
        type: 'ANSWER_RATING',
        referenceType: 'EXAM_ANSWER',
        referenceIdentifier: antwort.id,
        points: neuGehakt[i] ? antwort.max : frac(0),
      });
    });

    // — Kriterienstufen: linear in Punkte umgerechnet. Eine Rücknahme (`neu == null`) wird
    //   ausdrücklich als eigene Zeile mit **leeren** Punkten geschrieben, nicht ausgelassen:
    //   `ExamLog` ist anhängend, und ausgelassen hieße, dass `loadAssessment` nach einem
    //   Neustart die alte, längst zurückgenommene Stufe zurückläse. Relay kennt kein Löschen
    //   im Protokoll — die Rücknahme ist deshalb ein neuer Eintrag, keine Änderung. —
    eintrag.answers.forEach((antwort, i) => {
      if (antwort.steps === 0) return;
      const key = criterionKey(taskId, i);
      const alt = vorher.criteria[key];
      const neu = nachher.criteria[key];
      if (alt === neu) return;
      entwürfe.push({
        ...grund,
        type: 'ANSWER_RATING',
        referenceType: 'EXAM_ANSWER',
        referenceIdentifier: antwort.id,
        points: neu == null ? undefined : scaleStep(antwort.max, neu, antwort.steps),
      });
    });

    // — Handeingriff und globale Bewertung: Punktzahl direkt am ExamItem. Dieselbe Rücknahme
    //   wie oben bei den Kriterienstufen, hier aber mit Folgen: bliebe eine Rücknahme
    //   ungeschrieben, läse `loadAssessment` nach dem Neustart den alten Eingriff zurück und
    //   ließe ihn unbedingt gegen frisch erfasste Antworten gewinnen (`taskScore()`). Bewusst
    //   **nicht** über Festlegung 2 (die gerechnete Punktzahl schreiben) gelöst — das wäre
    //   mehrdeutig gegenüber einem Eingriff, der die gerechnete Zahl absichtlich trifft. —
    const altEingriff = vorher.override[taskId] ?? null;
    const neuEingriff = nachher.override[taskId] ?? null;
    if (altEingriff !== neuEingriff) {
      entwürfe.push({
        ...grund,
        type: 'QUESTION_RATING',
        referenceType: 'EXAM_ITEM',
        referenceIdentifier: eintrag.itemId,
        points: neuEingriff === null ? undefined : fromNumber(neuEingriff),
      });
    }

    // — Markierte Wortgruppen —
    const altWörter = JSON.stringify(wortIndexe(vorher, taskId));
    const neuWörter = JSON.stringify(wortIndexe(nachher, taskId));
    // Auch `[]` wird geschrieben: sonst bliebe das zuletzt abgewählte Wort nach dem Neustart
    // markiert, und der Server behielte es (Audit 2026-10-11, Fund 6).
    if (altWörter !== neuWörter) {
      entwürfe.push({
        ...grund,
        type: 'SELECTED_WORDS',
        referenceType: 'EXAM_QUESTION',
        referenceIdentifier: eintrag.questionId,
        value: neuWörter,
      });
    }

    // — Kommentartext —
    const altText = vorher.comments[taskId]?.text ?? '';
    const neuText = nachher.comments[taskId]?.text ?? '';
    if (altText !== neuText) {
      entwürfe.push({
        ...grund,
        type: 'TEXT_COMMENT',
        referenceType: 'EXAM_ITEM',
        referenceIdentifier: eintrag.itemId,
        value: neuText,
      });
    }

    // — Medien: eine Aufnahme des Prüfers ist eine Datei (`platform/storage.ts`, von der
    //   Oberfläche geschrieben), keine `Media`-Zeile — `Media` beschreibt Prüfungsinhalt, den
    //   die App nicht schreibt. Hier entsteht nur der Protokolleintrag, `value` trägt die
    //   **Anzahl** der Aufnahmen (s. `domain/medienzahl.ts`).
    //
    //   ⚠️ Verglichen werden **Zahlen**, nicht Zugänge. Bis zum 2026-09-14 stand hier
    //   `if (alt.has(medium.id)) continue` — ein Abgang erzeugte damit gar nichts, und eine
    //   weggeräumte Aufnahme liess ihre Zeile am Server auf eine Datei zeigen, die nie ankam
    //   (sie wandert ins Archiv, `platform/archive.ts`). Der Zahlenvergleich fängt beide
    //   Richtungen.
    //
    //   Die Handschrift fehlt hier bewusst: sie hat keine Liste von Einträgen wie Audio, Foto
    //   und Video (eine einzige, laufend wachsende Strichliste statt mehrerer Aufnahmen), ihr
    //   Diff-Stand sagt also nichts darüber, ob die Datei zum Zeitpunkt dieses Aufrufs schon
    //   existiert. Ihr Protokolleintrag entsteht deshalb nicht hier aus einem Vergleich zweier
    //   Assessment-Stände, sondern in `logHandwriting` unten, direkt beim Schreiben der Datei
    //   (`CommentSheet.tsx::sichereHandschrift`) — `await` erzwingt dort die Reihenfolge, ein
    //   späterer Vergleich hier könnte sie nur erhoffen. —
    for (const typ of LISTEN_LOG_TYPEN) {
      const altZahl = medienAnzahl(vorher, taskId, typ);
      const neuZahl = medienAnzahl(nachher, taskId, typ);
      if (altZahl === neuZahl) continue;
      entwürfe.push({
        ...grund,
        type: typ,
        referenceType: 'EXAM_ITEM',
        referenceIdentifier: eintrag.itemId,
        value: String(neuZahl),
      });
    }
  }

  /*
   * — Der Stationskommentar —
   *
   * Er steht im selben `comments`-Verzeichnis wie die Aufgabenkommentare, nur unter der
   * **Stationskennung** statt einer Aufgabenkennung. Deshalb steht er hier ausserhalb der
   * Schleife über den Aufgaben: `taskIndex` kennt ihn nicht.
   *
   * ⚠️ **Kein eigener Log-Typ.** `TEXT_COMMENT` bleibt `TEXT_COMMENT`; nur `referenceType`
   * sagt `EXAM_ITEM_CONTAINER` statt `EXAM_ITEM`, denn die Zeile führt beide Spalten selbst.
   * Einen Typ zu erfinden hiesse, Relay' Schema zu ergänzen — und die Quelle des Datenmodells
   * ist Relay, nicht diese App.
   */
  if (stationId) {
    const altText = vorher.comments[stationId]?.text ?? '';
    const neuText = nachher.comments[stationId]?.text ?? '';
    if (altText !== neuText) {
      entwürfe.push({
        ...grund,
        type: 'TEXT_COMMENT',
        referenceType: 'EXAM_ITEM_CONTAINER',
        referenceIdentifier: stationId,
        value: neuText,
      });
    }

    for (const typ of LISTEN_LOG_TYPEN) {
      const altZahl = medienAnzahl(vorher, stationId, typ);
      const neuZahl = medienAnzahl(nachher, stationId, typ);
      if (altZahl === neuZahl) continue;
      entwürfe.push({
        ...grund,
        type: typ,
        referenceType: 'EXAM_ITEM_CONTAINER',
        referenceIdentifier: stationId,
        value: String(neuZahl),
      });
    }
  }

  await store.tx(async (t) => {
    for (const entwurf of entwürfe) await appendLog(t, entwurf);
  });
  return entwürfe.length;
}

/** Baut aus dem Protokoll und den abgelegten Dateien den Bogen eines Prüflings. */
export async function loadAssessment(
  store: Store,
  index: TaskIndex,
  examId: string,
  candidateId: string,
  /** Die Station — ohne sie kommt der Stationskommentar nicht zurück (seit 2026-09-13). */
  stationId?: string,
): Promise<Assessment> {
  const a = emptyAssessment();
  const zeilen = await latestLogs(store, examId, candidateId);
  const nachZiel = new Map<string, ExamLogRow[]>();
  for (const zeile of zeilen) {
    const key = (zeile.referenceIdentifier ?? '') + '|' + zeile.examLogTypeID;
    nachZiel.set(key, [...(nachZiel.get(key) ?? []), zeile]);
  }
  const eine = (ziel: string, typ: string) => nachZiel.get(ziel + '|' + typ)?.[0];

  for (const [taskId, eintrag] of index) {
    const kasten: CommentBox = emptyComment();

    eintrag.answers.forEach((antwort, i) => {
      const zeile = eine(antwort.id, logTypeId('ANSWER_RATING'));
      if (!zeile || zeile.pointsNumerator === null || zeile.pointsDenominator === null) return;
      const punkte = frac(zeile.pointsNumerator, zeile.pointsDenominator);

      if (antwort.steps === 0) {
        a.checked[taskId] = a.checked[taskId] ?? eintrag.answers.map(() => false);
        a.checked[taskId][i] = punkte.n !== 0;
      } else {
        // Skalen sind linear ⇒ die Stufe ist aus der Punktzahl eindeutig zurückzurechnen.
        for (let stufe = 0; stufe < antwort.steps; stufe += 1) {
          if (eqFrac(scaleStep(antwort.max, stufe, antwort.steps), punkte)) {
            a.criteria[criterionKey(taskId, i)] = stufe;
            break;
          }
        }
      }
    });

    const eingriff = eine(eintrag.itemId, logTypeId('QUESTION_RATING'));
    if (eingriff && eingriff.pointsNumerator !== null && eingriff.pointsDenominator !== null) {
      a.override[taskId] = toNumber(frac(eingriff.pointsNumerator, eingriff.pointsDenominator));
    }

    const wörter = eine(eintrag.questionId, logTypeId('SELECTED_WORDS'));
    if (wörter) {
      for (const i of JSON.parse(wörter.value) as number[]) a.pickedWords[wordKey(taskId, i)] = true;
    }

    const text = eine(eintrag.itemId, logTypeId('TEXT_COMMENT'));
    if (text) kasten.text = text.value;

    // Aufnahmen sind Dateien, keine Zeile in einer Tabelle: `Media` beschreibt Prüfungsinhalt
    // (von Relay geladen, allenfalls als Ganzes gelöscht), den die App nicht schreibt. Der
    // ExamLog-Eintrag bleibt Protokoll, ist aber anhängend — ein gelöschtes Foto käme daraus
    // zurück. Zuordnung und Reihenfolge kommen deshalb aus dem Dateinamen selbst.
    for (const art of ['audio', 'photo', 'video'] as MediaKind[]) {
      // Über `alleDateien` statt einer eigenen, wortgleichen Kette: Filter und Sortierung der
      // Aufnahmen dürfen nicht an zwei Stellen stehen — sie entscheiden, welche Datei zu
      // welchem Prüfling und welcher Aufgabe gehört, und liefen sie auseinander, sähe der
      // Prüfer hier eine andere Liste als `deleteHandwriting`/`jüngsteDatei` sie sehen.
      const treffer = await alleDateien(art, candidateId, { taskId });
      kasten[MEDIA_FELD[art]] = treffer.map((x) => ({
        id: x.pfad,
        time: uhrzeit(x.name.date),
        fileName: x.pfad,
      }));
    }

    // Handschrift: anders als bei den Aufnahmen genügt kein Dateiname — die Schreibfläche
    // braucht die Striche selbst. Die jüngste Seite wird deshalb gelesen und über
    // `svgToStrokes` zurückverwandelt; fehlt eine Datei, bleibt die Seite leer.
    //
    // ⚠️ `readText` statt `readBase64` + `atob`: der Web-Rückfall des Dateisystems gibt eine
    // mit `Encoding.UTF8` geschriebene Datei nicht als Base64 zurück, `atob` warf dort also
    // bei jeder vorhandenen Seite — im Browser kam die Handschrift deshalb nie zurück
    // (am 2026-09-10 an der Markierebene aufgefallen, gleiche Ursache).
    const handschrift = await jüngsteDatei('handwriting', candidateId, taskId);
    if (handschrift) {
      // Eine unlesbare Seite kostet nur diese Seite, nicht die ganze Bewertung — wie beim
      // Stationskommentar unten (Audit 2026-10-11, Fund 14).
      const svg = await readText(handschrift.pfad).catch(() => '');
      if (svg) kasten.handwriting = svgToStrokes(svg);
    }

    a.comments[taskId] = kasten;
  }

  // — Der Stationskommentar — derselbe Kasten, nur unter der Stationskennung. Text aus dem
  // Protokoll, Aufnahmen aus den Dateinamen; beides wie bei einer Aufgabe, nur mit dem
  // Bezug `station-comment`.
  if (stationId) {
    const kasten: CommentBox = emptyComment();
    const text = eine(stationId, logTypeId('TEXT_COMMENT'));
    if (text) kasten.text = text.value;

    for (const art of ['audio', 'photo', 'video'] as MediaKind[]) {
      const treffer = await alleDateien(art, candidateId, { stationId });
      kasten[MEDIA_FELD[art]] = treffer.map((x) => ({
        id: x.pfad,
        time: uhrzeit(x.name.date),
        fileName: x.pfad,
      }));
    }

    const geschrieben = (await alleDateien('handwriting', candidateId, { stationId })).pop();
    if (geschrieben) {
      const svg = await readText(geschrieben.pfad).catch(() => '');
      if (svg) kasten.handwriting = svgToStrokes(svg);
    }

    if (kasten.text !== '' || kasten.audios.length || kasten.photos.length ||
        kasten.videos.length || kasten.handwriting.length) {
      a.comments[stationId] = kasten;
    }
  }

  return a;
}
